import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
/**
 * PLP V3/V4 header copied independently to contain the mobile banner redesign.
 * PlpBase supplies the existing explore links; local sheet state controls their presentation.
 * Desktop rendering continues to use the original banner and title layout.
 */
import { PlpBreadcrumb } from './PlpBreadcrumb';
import type { PlpBreadcrumbItem } from '../../utils/plpBreadcrumb';
import { PLP_DESKTOP_BANNER_CLASS, PLP_EXPLORE_CLOSE_FALLBACK_MS } from '../../constants';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { AppLink } from '@/shared/components/AppLink';
import type { ExploreOtherDesignsLink } from '@/shared/widgets/ExploreOtherDesignsWidget';
import { getCdnBaseUrl } from '@/core/utils/apiUtils';

export function PlpHeader({
  title,
  imageUrl,
  totalCount,
  countLabel = 'Designs',
  breadcrumbItems,
  renderDesktopBanner,
  showDesktopBanner = true,
  insetFromParent = false,
  exploreLinks = [],
}: {
  title: string;
  imageUrl?: string;
  totalCount?: number;
  countLabel?: string;
  breadcrumbItems?: PlpBreadcrumbItem[];
  renderDesktopBanner?: ReactNode;
  /** When false, desktop shows title/count only (no 1510×110 banner). Mobile hero is unchanged. */
  showDesktopBanner?: boolean;
  /** When true, parent shell provides desktop horizontal padding — skip duplicate inset. */
  insetFromParent?: boolean;
  exploreLinks?: ExploreOtherDesignsLink[];
}) {
  // Keep sheet visibility local; the same ID connects the trigger to the accessible dialog.
  const { analytics } = useAnalytics();
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const exploreId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Synchronize React visibility with the native modal API and restore page styles on cleanup.
  // SSR link rendering happens in the unconditional dialog JSX, independently of this effect.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isExploreOpen) {
      if (!dialog.open) dialog.showModal();
      const previousBodyOverflow = document.body.style.overflow;
      const previousHtmlOverflow = document.documentElement.style.overflow;
      const previousSheetOpen = document.documentElement.dataset.sheetOpen;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.dataset.sheetOpen = 'true';
      return () => {
        document.body.style.overflow = previousBodyOverflow;
        document.documentElement.style.overflow = previousHtmlOverflow;
        if (previousSheetOpen === undefined) delete document.documentElement.dataset.sheetOpen;
        else document.documentElement.dataset.sheetOpen = previousSheetOpen;
      };
    }
    // Keep the modal mounted through the existing exit animation; timer is a fallback.
    if (dialog.open) {
      const timeout = window.setTimeout(() => dialog.close(), PLP_EXPLORE_CLOSE_FALLBACK_MS);
      return () => window.clearTimeout(timeout);
    }
  }, [isExploreOpen]);

  const location = useLocation();
  const displayTitle = (title || 'Jewellery').trim().toUpperCase();
  const showBreadcrumb = Boolean(breadcrumbItems?.length);
  const resolvedImageUrl = imageUrl ?? `${getCdnBaseUrl()}assets/plp-header-bg.webp`;
  const desktopInsetClass = insetFromParent ? 'md:px-0' : '';

  return (
    <>
      {/* Mobile: pale blue banner with a diagonal white accent */}
      <section className="relative flex min-h-[70px] flex-shrink-0 items-center overflow-hidden bg-gradient-to-b from-[#dce9ff] to-[#eaf2ff] px-4 py-2 md:hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white to-[#f8fbff]"
          style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
        />{/* remove the image and add a gradient background with a diagonal white accent */}
        <div className="relative flex w-full items-center justify-between gap-3">
          {/* Long filter-derived titles are clamped to two lines so the fixed-width Explore button stays visible. */}
          <h1 className="min-w-0 line-clamp-2 font-serif text-lg font-normal tracking-tight text-[#15366F]">
            {displayTitle}
          </h1>
          {exploreLinks.length > 0 && (
            <button
              type="button"
              className="inline-flex shrink-0 items-center justify-center rounded-full border border-white bg-gradient-to-b from-[#f1f6ff] to-white px-4 py-2 text-sm shadow-[0_4px_12px_rgba(25,48,80,0.12)]"
              aria-expanded={isExploreOpen}
              aria-controls={exploreId}
              onClick={() => {
                if (!isExploreOpen) analytics?.trackEvent('Other Designs Selected', { 'CTA Name': 'Explore More' });
                setIsExploreOpen(open => !open);
              }}
            >{/* added explore more button to open the sheet with the links */}
              <span className="bg-gradient-to-r from-[#15366F] to-[#8bb3f7] bg-clip-text text-transparent">Explore More</span>
            </button>
          )}
        </div>
      </section>
      {/* Always render the closed dialog and links into the initial HTML, without a portal. */}
      <dialog
          ref={dialogRef}
          id={exploreId}
          aria-labelledby={`${exploreId}-title`}
          data-state={isExploreOpen ? 'open' : 'closed'}
          onCancel={(event) => { event.preventDefault(); setIsExploreOpen(false); }}
          onClose={() => setIsExploreOpen(false)}
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            const rect = event.currentTarget.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
              setIsExploreOpen(false);
            }
          }}
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget && !isExploreOpen) event.currentTarget.close();
          }}
          className="animate-bottom-sheet [&:not([open])]:hidden open:flex backdrop:bg-black/50 fixed inset-x-0 bottom-0 top-auto m-0 max-h-[85dvh] w-full max-w-none flex-col gap-0 overflow-hidden rounded-t-[28px] rounded-b-none border-0 bg-white p-0"
        >
          <div className="flex shrink-0 items-center justify-between gap-4 px-4 pb-3 pt-6">
            <h2 id={`${exploreId}-title`} className="font-primary text-xl font-bold text-[#15366F]">Explore More Designs</h2>
            <button type="button" onClick={() => setIsExploreOpen(false)} className="p-1 text-gray-900" aria-label="Close explore more designs">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav
            aria-label="Explore other designs"
            data-analytics-section-name="Explore other designs"
            className="min-h-0 overflow-y-auto pb-[env(safe-area-inset-bottom)]"
          >
            {exploreLinks.map((link, index) => {
              /* Highlight only a link matching the current path and query; clicking still uses AppLink navigation. */
              const selected = link.url === `${location.pathname}${location.search}`;
              return (
                <AppLink
                  key={`${link.url}-${index}`}
                  to={link.url}
                  shouldRefresh={false}
                  title={link.hoverTitle ?? undefined}
                  aria-current={selected ? 'page' : undefined}
                  onClick={() => setIsExploreOpen(false)}
                  className={`flex min-h-[52px] items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 text-sm text-[#15366F] ${selected ? 'bg-[#f0f4f8]' : 'bg-white'}`}
                >
                  {link.label}
                  {selected && <Check className="h-4 w-4 shrink-0" aria-hidden />}
                </AppLink>
              );
            })}
          </nav>
      </dialog>

      {/* Desktop: breadcrumb → 1510×110 banner → title | count */}
      <section className={`hidden flex-shrink-0 flex-col pt-4 md:flex px-4 ${desktopInsetClass}`}>
        {showBreadcrumb && breadcrumbItems ? (
          <div className={`px-4 pb-3 ${desktopInsetClass}`}>
            <PlpBreadcrumb items={breadcrumbItems} />
          </div>
        ) : null}{/* existing */}

        {showDesktopBanner ? (
          <div className={`${PLP_DESKTOP_BANNER_CLASS} overflow-hidden rounded-sm bg-[#E8E4DF] p-[2px] shadow-[0_0_5px_1px_rgba(0,0,0,0.1)]`}>
            {renderDesktopBanner ?? (
              <img
                src={resolvedImageUrl}
                alt=""
                className="h-full w-full object-cover object-[bottom_right]"
                decoding="async"
              />
            )}
          </div>
        ) : null}{/* existing */}

        <div className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 pb-4 ${desktopInsetClass} ${showDesktopBanner ? 'pt-4' : 'pt-0'}`}>          <h1 className="font-serif text-3xl font-normal tracking-tight text-[#103064]">
            {displayTitle}
          </h1>
          {totalCount != null ? (
            <>
              <span className="text-gray-300" aria-hidden>
                |
              </span>
              <span className="text-base text-gray-500">
                {totalCount} {countLabel}
              </span>
            </>
          ) : null}{/* existing */}
        </div>
      </section>
    </>
  );
}
